/**
 * Course settings · "Feedback de final" (30.10.2026): the one switch for the
 * questionnaire's automatic emails, off until the owner turns it on, and the
 * report on every group that had it, with its Excel, and the testimonials.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, MessageSquareHeart, ToggleLeft, ToggleRight } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { downloadBlob } from "../../../lib/downloadBlob";
import { SWITCH_HINT, SWITCH_TITLE, modeWord } from "../../../lib/finalFeedbackAdmin";
import TestimonialsCard from "./TestimonialsCard";

const signed = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}`);
const pct = (n) => (n == null ? "—" : `${n}%`);

export default function FeedbackSettings({ canMutate }) {
  const [enabled, setEnabled] = useState(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get("/admin/final-feedback/settings"), api.get("/admin/final-feedback/report")]);
      setEnabled(Boolean(s.data?.enabled));
      setRows(r.data?.rows || []);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!canMutate || busy) return;
    setBusy(true);
    try {
      const r = await api.put("/admin/final-feedback/settings", { enabled: !enabled });
      setEnabled(Boolean(r.data?.enabled));
      toast.success(r.data?.enabled ? "Chestionarul de feedback pleacă automat." : "Chestionarul de feedback nu mai pleacă automat.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva comutatorul."));
    } finally {
      setBusy(false);
    }
  };

  if (failed) return <p className="text-sm text-[#6B7280]">Nu am putut încărca setarea.</p>;
  if (enabled === null) {
    return <p className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 className="animate-spin" size={16} /> Se încarcă…</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="settings-tab-feedback">
      <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#965EA7]">
          <MessageSquareHeart size={16} /> Feedback de final
        </div>
        <div className="flex items-start justify-between gap-3 rounded-xl border border-[#965EA7]/20 bg-[#F0E7F4]/40 p-4">
          <div className="min-w-0">
            <div className="font-semibold text-[#373A45]">{SWITCH_TITLE}</div>
            <div className="mt-1 max-w-2xl text-xs text-[#6B7280]">{SWITCH_HINT}</div>
          </div>
          <button type="button" role="switch" aria-checked={enabled} aria-label={SWITCH_TITLE}
            disabled={!canMutate || busy} onClick={toggle} className="shrink-0 text-[#965EA7] disabled:opacity-40"
            data-testid="settings-feedback-toggle">
            {enabled ? <ToggleRight size={40} /> : <ToggleLeft size={40} />}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="feedback-report">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-[#373A45]">Rezultatele pe grupe</h3>
          {rows.length > 0 && (
            <button type="button" disabled={exporting}
              onClick={() => downloadBlob("/admin/final-feedback/report/export", "Feedback de final - toate grupele.xlsx", setExporting)}
              className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50">
              {exporting ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />} Excel
            </button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="mt-2 text-sm text-[#6B7280]">Încă nicio grupă nu a avut chestionarul de feedback în platformă.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F6F6] text-left text-xs uppercase tracking-wide text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2">Grupa</th><th className="px-3 py-2">Formator</th><th className="px-3 py-2">Luna</th>
                  <th className="px-3 py-2">Au răspuns</th><th className="px-3 py-2">NPS</th><th className="px-3 py-2">Mulțumire</th>
                  <th className="px-3 py-2" title="Excelent + Foarte bun, la întrebarea 5">Claritate</th>
                  <th className="px-3 py-2" title="Excelent + Foarte bun, la întrebarea 6">Disponibilitate</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {rows.map((r) => (
                  <tr key={r.group_id} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-2 text-[#373A45]">{r.group_name}<span className="block text-[11px] text-[#6B7280]">{modeWord(r.mode)}</span></td>
                    <td className="px-3 py-2 text-[#373A45]">{r.trainer || "—"}</td>
                    <td className="px-3 py-2 text-[#6B7280]">{r.month}</td>
                    <td className="px-3 py-2 text-[#373A45]">{r.answered} din {r.people}</td>
                    <td className="px-3 py-2 font-semibold text-[#373A45]">{signed(r.nps)}</td>
                    <td className="px-3 py-2 text-[#373A45]">{r.satisfaction == null ? "—" : String(r.satisfaction).replace(".", ",")}</td>
                    <td className="px-3 py-2 text-[#373A45]">{pct(r.clarity_good)}</td>
                    <td className="px-3 py-2 text-[#373A45]">{pct(r.support_good)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TestimonialsCard canMutate={canMutate} />
    </div>
  );
}
